'use client'
import { useEffect, useState } from 'react'
import { ChevronDown, Search } from '../icons'
import { cn } from '../lib/utils'

interface SearchableDropdownItem {
  id: string | number
  label: string
  description?: string
}

interface SearchableDropdownProps {
  items: SearchableDropdownItem[]
  value?: string | number
  placeholder?: string
  searchPlaceholder?: string
  onSelect: (item: SearchableDropdownItem) => void
  disabled?: boolean
  label?: string
  required?: boolean
  error?: string
  className?: string
}

export function SearchableDropdown({
  items,
  value,
  placeholder = 'Selecciona una opción...',
  searchPlaceholder = 'Buscar...',
  onSelect,
  disabled = false,
  label,
  required = false,
  error,
  className,
}: SearchableDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItem, setSelectedItem] =
    useState<SearchableDropdownItem | null>(null)

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSelect = (item: SearchableDropdownItem) => {
    setSelectedItem(item)
    onSelect(item)
    setIsOpen(false)
    setSearchTerm('')
  }

  useEffect(() => {
    if (value) {
      const found = items.find((item) => item.id === value)
      if (found) {
        setSelectedItem(found)
      }
    } else {
      setSelectedItem(null)
    }
  }, [value, items])

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="text-sm font-medium leading-none">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm transition-colors focus:border-admin-blue focus:outline-none focus:ring-1 focus:ring-admin-blue',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 hover:border-gray-400',
            disabled && 'cursor-not-allowed bg-gray-50 opacity-50'
          )}>
          <span
            className={cn(
              'text-sm',
              selectedItem ? 'text-gray-900' : 'text-gray-500'
            )}>
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-500 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="sticky top-0 border-b border-gray-200 bg-white p-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-admin-blue focus:outline-none focus:ring-1 focus:ring-admin-blue"
                />
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto py-1">
              {filteredItems.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No se encontraron opciones
                </div>
              ) : (
                filteredItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors focus:outline-none',
                      selectedItem?.id === item.id
                        ? 'bg-blue-100 font-medium text-blue-800'
                        : 'hover:bg-gray-50'
                    )}>
                    <div className="font-medium">{item.label}</div>
                    {item.description && (
                      <div
                        className={cn(
                          'mt-1 text-sm',
                          selectedItem?.id === item.id
                            ? 'text-white/80'
                            : 'text-gray-500'
                        )}>
                        {item.description}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}